import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CurrencyDollar, EnvelopeSimple, MagicWand } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton } from "../components/Brutal";

const fmt = (v) => (v === null || v === undefined ? "" : String(v));

/** v414.0 — org USD / MXN retail price sheets. US & Mexico distributors'
 *  stores price from these instead of the CAD retail. */
export default function PriceSheetsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [drafts, setDrafts] = useState({}); // `${pid}:${cur}` -> string
  const [rate, setRate] = useState("");
  const [rateCur, setRateCur] = useState("USD");
  const [filling, setFilling] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = () =>
    api.get("/price-sheets")
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { load(); }, []);

  const save = async (pid, currency, field, raw) => {
    const key = `${pid}:${currency}:${field}`;
    const trimmed = String(raw).trim();
    const num = trimmed === "" ? null : Number(trimmed);
    if (num !== null && (!Number.isFinite(num) || num < 0)) {
      toast.error(t("sheets.invalid_price"));
      return;
    }
    try {
      await api.put(`/price-sheets/${pid}`, { currency, [field]: num });
      setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
      const col = field === "wholesale_price"
        ? `${currency.toLowerCase()}_ws` : currency.toLowerCase();
      setRows((rs) => rs.map((r) => r.product_id === pid ? { ...r, [col]: num } : r));
      toast.success(num === null ? t("sheets.cleared") : t("sheets.saved", { price: num, currency }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const autofill = async (overwrite) => {
    const r = Number(rate);
    if (!Number.isFinite(r) || r <= 0) { toast.error(t("sheets.invalid_rate")); return; }
    if (overwrite && !window.confirm(t("sheets.overwrite_confirm", { currency: rateCur }))) return;
    setFilling(true);
    try {
      const { data } = await api.post("/price-sheets/autofill", { currency: rateCur, rate: r, overwrite });
      toast.success(t("sheets.filled", { n: data.filled, currency: rateCur }));
      setDrafts({});
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFilling(false);
    }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/price-sheets/digest/send", {});
      toast.success(t("sheets.digest_sent", { n: data.sent_to.length, changes: data.changes }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingDigest(false);
    }
  };

  const cell = (row, currency, field) => {
    const key = `${row.product_id}:${currency}:${field}`;
    const col = field === "wholesale_price" ? `${currency.toLowerCase()}_ws` : currency.toLowerCase();
    const saved = row[col];
    const val = drafts[key] !== undefined ? drafts[key] : fmt(saved);
    return (
      <input
        type="number" min="0" step="0.01" value={val}
        onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
        onBlur={(e) => { if (drafts[key] !== undefined && drafts[key] !== fmt(saved)) save(row.product_id, currency, field, e.target.value); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        placeholder="—"
        className={`w-20 px-2 py-1.5 rounded-lg border text-sm text-right tabular-nums focus:outline-none focus:border-[#C9A84C] ${saved == null && drafts[key] === undefined ? "border-amber-300 bg-amber-50/60" : "border-zinc-300"}`}
        data-testid={`sheet-${col}-${row.product_id}`}
      />
    );
  };

  if (rows === null) {
    return <div className="p-8 text-sm text-zinc-500">{t("common.loading")}</div>;
  }

  const missing = (cur) => rows.filter((r) => r[cur] == null || r[`${cur}_ws`] == null).length;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4" data-testid="price-sheets-page">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <CurrencyDollar size={24} weight="bold" className="text-[#8a6a1c]" />
          {t("sheets.title")}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">{t("sheets.subtitle")}</p>
        <p className="text-xs text-zinc-500 mt-1">
          {t("sheets.missing", { usd: missing("usd"), mxn: missing("mxn") })}
        </p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-wrap items-end gap-2">
        <div>
          <label className="block text-xs font-semibold mb-1">{t("sheets.rate_currency")}</label>
          <select className="input-brutal" value={rateCur} onChange={(e) => setRateCur(e.target.value)} data-testid="sheet-rate-currency">
            <option value="USD">USD</option>
            <option value="MXN">MXN</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">{t("sheets.rate_label")}</label>
          <input type="number" min="0" step="0.0001" value={rate} onChange={(e) => setRate(e.target.value)}
            placeholder={rateCur === "USD" ? "0.75" : "13.5"}
            className="w-28 px-3 py-2 rounded-lg border border-zinc-300 text-sm focus:outline-none focus:border-[#C9A84C]"
            data-testid="sheet-rate-input" />
        </div>
        <BrutalButton size="sm" onClick={() => autofill(false)} disabled={filling} data-testid="sheet-fill-empty">
          <MagicWand size={14} weight="bold" /> {t("sheets.fill_empty")}
        </BrutalButton>
        <BrutalButton size="sm" variant="secondary" onClick={() => autofill(true)} disabled={filling} data-testid="sheet-fill-all">
          {t("sheets.fill_all")}
        </BrutalButton>
        <BrutalButton size="sm" variant="secondary" onClick={sendDigest} disabled={sendingDigest} data-testid="sheet-digest-send-btn">
          <EnvelopeSimple size={14} weight="bold" /> {t("sheets.digest_send")}
        </BrutalButton>
        <p className="basis-full text-[11px] text-zinc-400">{t("sheets.rate_hint")}</p>
        <p className="basis-full text-[11px] text-zinc-400 -mt-1">{t("sheets.digest_hint")}</p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b-2 border-[#c9a24a]/60">
              <th className="px-3 py-2">{t("sheets.col_product")}</th>
              <th className="px-3 py-2 text-right">CAD (WS / {t("sheets.retail")})</th>
              <th className="px-3 py-2 text-right">USD WS</th>
              <th className="px-3 py-2 text-right">USD {t("sheets.retail")}</th>
              <th className="px-3 py-2 text-right">MXN WS</th>
              <th className="px-3 py-2 text-right">MXN {t("sheets.retail")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.product_id} className="border-b border-zinc-100" data-testid={`sheet-row-${r.product_id}`}>
                <td className="px-3 py-1.5">
                  <p className="font-medium">{r.name}</p>
                  <p className="text-[10px] text-zinc-400">{r.category}</p>
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-zinc-500 whitespace-nowrap">
                  {r.cad_ws != null ? `$${r.cad_ws}` : "—"} / {r.cad_retail != null ? `$${r.cad_retail}` : "—"}
                </td>
                <td className="px-3 py-1.5 text-right">{cell(r, "USD", "wholesale_price")}</td>
                <td className="px-3 py-1.5 text-right">{cell(r, "USD", "retail_price")}</td>
                <td className="px-3 py-1.5 text-right">{cell(r, "MXN", "wholesale_price")}</td>
                <td className="px-3 py-1.5 text-right">{cell(r, "MXN", "retail_price")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
